import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable, of, Subscription, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';

import {
  CONTENT_TYPE_JSON_UTF8,
  TOTAL_BOOKMARK_INFO_API_URL,
  TOTAL_LINK_INFO_API_URL,
  TOTAL_SEARCH_API_URL
} from '../../defines/protocol';
import {ApiService} from '../api/api.service';
import {NormalizeSearchKeyword} from '../../defines/client.func';
import {SimpleWord} from '../../models/simple-word.model';
import {Word} from "../../models/word.model";
import {Video} from '../../models/video.model';
import {SimpleDialect} from '../../models/dialect.model';
import {Proverb} from '../../models/proverb.model';
import {Saseung3} from '../../models/saseung3.model';
import {Example} from '../../models/example.model';


export type TotalSearch = {
  countWord?: number,
  listWord?: Array<SimpleWord>,
  countVideo?: number,
  listVideo?: Array<Video>,
  countDialect?: number,
  listDialect?: Array<SimpleDialect>,
  countProverb?: number,
  listProverb?: Array<Proverb>,
  countSaseung?: number,
  listSaseung?: Array<Saseung3>,
  countExample?: number,
  listExample?: Array<Example>,
};

export type BookmarkInfo = {
  numWordBookmark?: number;
  numVideoBookmark?: number;
  numDialectBookmark?: number;
  numProverbBookmark?: number;
  numSaseungBookmark?: number;
  numSajaseungeoBookmark?: number;
};

export type TotalLinkParam = {
  wordIds?: Array<string>,
  dialectIds?: Array<string>
};

export type TotalLinkInfo = {
  word?: Array<Word>,
  dialect?: Array<SimpleDialect>,
};

@Injectable({
  providedIn: 'root'
})
export class TotalService {
  private bookmarkInfoSubject = new BehaviorSubject<BookmarkInfo | null>(null);
  public bookmarkInfo$ = this.bookmarkInfoSubject.asObservable();

  constructor(
    private api: ApiService,
  ) { }


  public GetTotalSearch(keyword: string): Observable<TotalSearch> {
    const url = TOTAL_SEARCH_API_URL + NormalizeSearchKeyword(keyword) + '/';
    return this.api.Get(url).pipe(
      map(data => {
        console.log('TotalService::GetTotalSearch()', url, data);

        const totalSearch: TotalSearch = {};
        totalSearch.countWord = data.word_count ?? null;
        if (data?.word_list) {
          totalSearch.listWord = new Array<SimpleWord>();
          data.word_list.forEach((value: any) => totalSearch.listWord?.push(new SimpleWord(value)));
        }

        totalSearch.countVideo = data.video_count ?? null;
        if (data?.video_list) {
          totalSearch.listVideo = new Array<Video>();
          data.video_list.forEach((value: any) => totalSearch.listVideo?.push(new Video(value)));
        }

        totalSearch.countDialect = data.dialect_count ?? null;
        if (data?.dialect_list) {
          totalSearch.listDialect = new Array<SimpleDialect>();
          data.dialect_list.forEach((value: any) => totalSearch.listDialect?.push(new SimpleDialect(value)));
        }

        totalSearch.countProverb = data.proverb_count ?? null;
        if (data?.proverb_list) {
          totalSearch.listProverb = new Array<Proverb>();
          data.proverb_list.forEach((value: any) => totalSearch.listProverb?.push(new Proverb(value)));
        }

        totalSearch.countSaseung = data.saseung_count ?? null;
        if (data?.saseung_list) {
          totalSearch.listSaseung = new Array<Saseung3>();
          data.saseung_list.forEach((value: any) => totalSearch.listSaseung?.push(new Saseung3(value)));
        }

        if (Number.isSafeInteger(data?.example_count) && data.example_count >= 0) {
          totalSearch.countExample = data.example_count;
        } else if (data?.example_count != null) {
          // OpenAPI상 example_count는 정수이며 음수 표기는 UI에 부적합 — 매핑 생략 + 알림.
          // Number.isSafeInteger는 typeof number + finite + 정수 + 2^53 안전 범위까지 한 번에 검사한다.
          console.error(`TotalService::GetTotalSearch(${url}) invalid example_count`, typeof data.example_count, data.example_count);
        }
        if (Array.isArray(data?.example_list)) {
          totalSearch.listExample = new Array<Example>();
          data.example_list.forEach((value: any, index: number) => {
            if (value == null || typeof value !== 'object') {
              // 항목이 객체가 아니면 Example 생성자가 빈 인스턴스를 만들어 silent하게 통과한다.
              console.error(`TotalService::GetTotalSearch(${url}) invalid example_list item`, index, value);
              return;
            }
            totalSearch.listExample?.push(new Example(value));
          });
        } else if (data?.example_list != null) {
          // 배열이 아닌 truthy 응답(string/object 등)은 매핑 불가 — silent ignore 대신 알림.
          // production 빌드(main.ts)는 console.warn을 무력화하므로 console.error 사용.
          console.error(`TotalService::GetTotalSearch(${url}) example_list is not an array`, typeof data.example_list, data.example_list);
        }
        if (typeof data?.example_count === 'number' && data.example_count > 0 && !Array.isArray(data?.example_list)) {
          // count는 양수인데 list가 없으면 UI가 "N개"만 표시하고 항목이 0건인 silent 실패가 된다.
          console.error(`TotalService::GetTotalSearch(${url}) example_count > 0 but example_list is missing`, data.example_count, data?.example_list);
        }
        if (Array.isArray(data?.example_list) && data?.example_count == null) {
          // list가 도착했는데 count가 없으면 UI 헤더(N개)가 표시되지 않아 항목만 떠 있는 silent 실패가 된다.
          console.error(`TotalService::GetTotalSearch(${url}) example_list is present but example_count is missing`, data.example_list.length, data?.example_count);
        }
        if (
          totalSearch.countExample !== undefined &&
          totalSearch.listExample !== undefined &&
          totalSearch.countExample < totalSearch.listExample.length
        ) {
          // count는 전체 검색 결과 수, list는 통합 검색 미리보기 목록이다.
          // 미리보기라 list가 count보다 짧은 것은 정상이며, 초과하는 경우만 API 계약 불일치로 본다.
          console.error(
            `TotalService::GetTotalSearch(${url}) example_list length exceeds example_count`,
            totalSearch.countExample,
            totalSearch.listExample.length,
          );
        }
        return totalSearch;
      })
    );
  }


  /**
   * 각종 책갈피 정보를 받아오는 함수
   */
  public GetBookmarkInfo(): Observable<BookmarkInfo> {
    return this.api.Get(TOTAL_BOOKMARK_INFO_API_URL).pipe(
      map(data => {
        console.log('Total::Bookmark', data);
        const bmInfo: BookmarkInfo = {};
        if ('num_word_bookmark' in data) {
          bmInfo.numWordBookmark = data.num_word_bookmark;
          bmInfo.numVideoBookmark = data.num_video_bookmark;
          bmInfo.numDialectBookmark = data.num_dialect_bookmark;
          bmInfo.numProverbBookmark = data.num_proverb_bookmark;
          bmInfo.numSaseungBookmark = data.num_saseung_bookmark;
          bmInfo.numSajaseungeoBookmark = data.num_sajaseungeo_bookmark;
        }
        this.bookmarkInfoSubject.next(bmInfo);
        return bmInfo;
      }),
      // catchError((err, caught) => {
      //   console.error('sendBookmark', err);
      //   throwError(err);
      //   return caught;
      // })
    );
  }

  public updateProverbBookmarkCount(add: boolean): void {
    const bmInfo = this.bookmarkInfoSubject.value;
    if (!bmInfo) {
      // GetBookmarkInfo() 호출 전이면 무시한다. 이후 조회 시 서버 값으로 갱신된다.
      return;
    }

    const numProverbBookmark = Math.max(0, (bmInfo.numProverbBookmark ?? 0) + (add ? 1 : -1));
    this.bookmarkInfoSubject.next({
      ...bmInfo,
      numProverbBookmark,
    });
  }

  public updateSaseungBookmarkCount(add: boolean): void {
    const bmInfo = this.bookmarkInfoSubject.value;
    if (!bmInfo) {
      // GetBookmarkInfo() 호출 전이면 무시한다. 이후 조회 시 서버 값으로 갱신된다.
      return;
    }

    const numSaseungBookmark = Math.max(0, (bmInfo.numSaseungBookmark ?? 0) + (add ? 1 : -1));
    this.bookmarkInfoSubject.next({
      ...bmInfo,
      numSaseungBookmark,
    });
  }

  public updateSajaseungeoBookmarkCount(add: boolean): void {
    const bmInfo = this.bookmarkInfoSubject.value;
    if (!bmInfo) {
      // GetBookmarkInfo() 호출 전이면 무시한다. 이후 조회 시 서버 값으로 갱신된다.
      return;
    }

    const numSajaseungeoBookmark = Math.max(0, (bmInfo.numSajaseungeoBookmark ?? 0) + (add ? 1 : -1));
    this.bookmarkInfoSubject.next({
      ...bmInfo,
      numSajaseungeoBookmark,
    });
  }


  /**
   * 단어와 사투리의 링크 정보를 받아오는 함수
   * @param linkParam
   * @constructor
   */
  public GetLinkInfo(linkParam: TotalLinkParam): Observable<TotalLinkInfo> {
    if ((linkParam?.wordIds?.length ?? 0) === 0 && (linkParam?.dialectIds?.length ?? 0) === 0) {
      return throwError(new Error('Both wordIds and dialectIds are empty or not provided.'));
    }
    return this.api.Post(TOTAL_LINK_INFO_API_URL, {
      ...(linkParam?.wordIds ? {word : linkParam.wordIds} : {}),
      ...(linkParam?.dialectIds ? {dialect : linkParam.dialectIds} : {}),
    }, null, CONTENT_TYPE_JSON_UTF8).pipe(
      map(data => {
        console.log('Total::Link', data);

        const linkInfo: TotalLinkInfo = {};

        if (data?.word) {
          linkInfo.word = data.word.map((value: any) => new Word(value));
        }
        if (data?.dialect) {
          linkInfo.dialect = data.dialect.map((value: any) => new SimpleDialect(value));
        }

        return linkInfo;
      })
    );
  }
}
